import { isDefined } from "@clipboard-health/util-ts";
import { isCbhApiAxiosError } from "@src/appV2/Errors/errorUtils";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { openZendeskMessaging } from "@src/appV2/lib/ZendeskMessaging/openZendeskMessaging";
import { useCallback, useMemo } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { usePatchExtraWorkedTimeRequestMetadata } from "../../api/usePatchExtraWorkedTimeRequestMetadata";
import { useMissedPunchRequestGeofenceCheck } from "../../hooks/useMissedPunchRequestGeofenceCheck";
import { SUBMIT_UNVERIFIED_TIMESHEET_FULL_PATH } from "../../path";
import { SKIP_WORKPLACE_SIGNATURE_QUERY_PARAM_KEY } from "../../ShiftDetails/constants";

interface UsePastShiftDetailsActionsParams {
  shiftId: string;
  shiftEnd: string;
  workerId: string;
  workplaceId: string | undefined;
  workplaceLocation: { latitude: number; longitude: number } | undefined;
  requestId?: string;
  onWorkerOutsideGeofence: () => void;
  onAcceptOriginalTimesSuccess: () => void;
}

export function usePastShiftDetailsActions(params: UsePastShiftDetailsActionsParams) {
  const {
    shiftId,
    shiftEnd,
    workerId,
    workplaceId,
    workplaceLocation,
    requestId,
    onWorkerOutsideGeofence,
    onAcceptOriginalTimesSuccess,
  } = params;

  const history = useHistory();
  const { isCheckingGeofence, checkGeofence } = useMissedPunchRequestGeofenceCheck();
  const { showErrorToast } = useToast();

  const { mutate: patchExtraWorkedTimeRequestMetadata, isLoading: isAcceptingOriginalTimes } =
    usePatchExtraWorkedTimeRequestMetadata({
      onSuccess: () => {
        onAcceptOriginalTimesSuccess();
      },
      onError: (error) => {
        let errorMessage = "Something went wrong. Please try again.";
        if (isCbhApiAxiosError(error)) {
          const detail = error.response.data.errors[0]?.detail;
          errorMessage = detail ?? errorMessage;
        }

        showErrorToast(errorMessage);
      },
    });

  const geofenceParams = useMemo(
    () => ({ shiftId, shiftEnd, workerId, workplaceId, workplaceLocation }),
    [shiftId, shiftEnd, workerId, workplaceId, workplaceLocation]
  );

  const navigateToSubmitTimesheet = useCallback(
    (skipWorkplaceSignature: boolean) => {
      const searchParams = new URLSearchParams({
        [SKIP_WORKPLACE_SIGNATURE_QUERY_PARAM_KEY]: String(skipWorkplaceSignature),
      });
      history.push({
        pathname: generatePath(SUBMIT_UNVERIFIED_TIMESHEET_FULL_PATH, { shiftId }),
        search: searchParams.toString(),
      });
    },
    [history, shiftId]
  );

  const handleGeofenceGatedSubmit = useCallback(
    async (action: string) => {
      logEvent(APP_V2_USER_EVENTS.PAST_SHIFT_DETAILS_ACTION_TAPPED, { shiftId, action });
      const result = await checkGeofence(geofenceParams);
      if (result.status === "inside" || result.status === "unknown") {
        navigateToSubmitTimesheet(false);
      } else {
        logEvent(APP_V2_USER_EVENTS.MPR_RETURN_FOR_SIGNATURE_SHOWN, { shiftId, workerId });
        onWorkerOutsideGeofence();
      }
    },
    [
      checkGeofence,
      geofenceParams,
      navigateToSubmitTimesheet,
      onWorkerOutsideGeofence,
      shiftId,
      workerId,
    ]
  );

  const handleRecheckLocation = useCallback(async () => {
    const result = await checkGeofence(geofenceParams);
    if (result.status === "inside" || result.status === "unknown") {
      navigateToSubmitTimesheet(false);
      return true;
    }

    return false;
  }, [checkGeofence, geofenceParams, navigateToSubmitTimesheet]);

  const onSubmitNewRequest = useCallback(() => {
    void handleGeofenceGatedSubmit("submit_new_request");
  }, [handleGeofenceGatedSubmit]);

  const onRequestPayAdjustment = useCallback(() => {
    void handleGeofenceGatedSubmit("request_pay_adjustment");
  }, [handleGeofenceGatedSubmit]);

  const onAcceptOriginalTimes = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.PAST_SHIFT_DETAILS_ACTION_TAPPED, {
      shiftId,
      action: "accept_original_times",
    });
    if (isDefined(requestId)) {
      patchExtraWorkedTimeRequestMetadata({
        extraWorkedTimeRequestId: requestId,
        metadata: { isNotificationDismissed: true, isOutcomeAcknowledged: true },
      });
    }
  }, [shiftId, requestId, patchExtraWorkedTimeRequestMetadata]);

  const onContactSupport = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.PAST_SHIFT_DETAILS_ACTION_TAPPED, {
      shiftId,
      action: "contact_support",
    });
    void openZendeskMessaging();
  }, [shiftId]);

  return {
    onSubmitNewRequest,
    onAcceptOriginalTimes,
    onContactSupport,
    onRequestPayAdjustment,
    handleRecheckLocation,
    navigateToSubmitTimesheet,
    isCheckingGeofence,
    isAcceptingOriginalTimes,
  };
}
